import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { invalidateEndpointCache } from '@/api/endpointCache'

/**
 * The slice of a TanStack `UseQueryResult` this hook needs.
 *
 * Structural, exactly like `RefreshSource` in useRefreshState.ts and for the
 * same reason: a screen hands over the query object it already holds, and
 * nothing here needs a generic over TData. `refetch` resolves with the query
 * result rather than rejecting, so the return type is deliberately `unknown` —
 * this hook never reads it.
 */
export interface RefreshableQuery {
  refetch: () => Promise<unknown>
}

export interface ManualRefresh {
  /**
   * True from the click until every refetch it started has settled. Hand it
   * straight to `useRefreshState`'s second argument: that hook cannot tell a
   * member's Refresh from the 20s poll tick on its own, and this is the flag
   * it asks the caller to keep. See its own doc comment.
   */
  isManual: boolean
  /** The click handler. Fire-and-forget: it never throws and never rejects. */
  refresh: () => void
}

/**
 * A refresh the MEMBER asked for — one that is required to reach the network.
 *
 * ── The bug this exists to close ─────────────────────────────────────────────
 *
 * `api/endpointCache.ts` gives every raw endpoint its own cache entry with a
 * `ENDPOINT_STALE_MS` window of `config.livePollMs / 2` (10s today). A screen's
 * `queryFn` fans out through that cache, so an outer `query.refetch()` on its
 * own re-enters the fan-out, finds every endpoint entry fresh, and resolves in
 * microseconds having fetched nothing at all.
 *
 * On `/market` that reads as follows: the 20s poll lands at t=0, at t=+4s the
 * member distrusts the numbers and clicks Refresh, all ten endpoint entries are
 * 4s old, the promise settles immediately, `dataUpdatedAt` moves and RefreshBar
 * prints "Updated just now" over byte-identical data. For up to 10s after every
 * poll tick and every focus refetch, the refresh affordance on the two most
 * price-sensitive screens was a no-op that claimed success.
 *
 * Invalidating first is what makes the claim true again: the entries are marked
 * stale, the refetch that follows re-runs the fan-out, and the fan-out goes to
 * the wire. The ORDER is the whole fix — invalidating after the refetch, or
 * alongside it in a `Promise.all`, races the fan-out it is meant to precede.
 *
 * ── Why a hook, and not four invalidate calls ────────────────────────────────
 *
 * There were four refresh affordances (OverviewScreen's Retry, LiveMarketScreen's
 * Refresh button and its Retry, the old Home index card's refresh icon) and each one was three
 * lines of the same flag-setting dance around `query.refetch()`. Adding a fifth
 * line to each of them is an invitation for the fifth affordance to be written
 * without it, and the failure is silent — the bar says "Updated just now" either
 * way. So the invalidate lives on the far side of a seam nobody has to remember:
 * a screen that wants a member-initiated refresh calls this, and there is no
 * shorter path that also compiles into a correct one.
 *
 * It absorbs the `isManualRefresh` flag at the same time, because that flag
 * answers exactly the same question this hook exists to answer — was this fetch
 * asked for by a person — and keeping the two apart meant every screen restated
 * the coupling by hand.
 *
 * ── Why EVERY endpoint entry, and not just this screen's ─────────────────────
 *
 * There is no honest way to invalidate "only the endpoints this screen needs".
 * The fan-out lives inside the services (`overviewService.overview` reaches ten
 * endpoints, `marketService.indexSummary` four), and the set is not visible from
 * a component. Writing it down here would create a second, hand-maintained copy
 * of a dependency list that already exists in the service — and it would drift
 * the first time a service added an endpoint, silently, back into exactly the
 * bug above.
 *
 * Over-invalidating is cheap here, and cheaper than it looks. `ENDPOINT_STALE_MS`
 * is already strictly below `config.livePollMs`, so every other screen's poll
 * tick was going to the wire regardless; the only thing thrown away is the
 * sub-10s window in which two fan-outs SHARE an entry. That window reopens the
 * instant this refresh repopulates the entries, so a concurrent screen's next
 * tick shares again. The worst case is one extra request per other mounted
 * screen, once, on a click a member made on purpose — against the certainty of a
 * refresh button that lies.
 *
 * ── Which QueryClient ────────────────────────────────────────────────────────
 *
 * The endpoint entries live on whichever client the SERVICE wrote them to, so
 * this has to invalidate that same one or it invalidates an empty cache. The app
 * mounts exactly one client (`app/providers.tsx`), which is both what
 * `useQueryClient` returns here and what `marketEndpoints()` defaults to, so the
 * two are the same object in the app. Reading it off the provider rather than
 * importing the singleton is the idiom `useMarketStatus` already established,
 * and it is what stays correct when a `queries.ts` starts passing `{ client }`
 * through to its service.
 *
 * The cost of that choice is a real constraint on tests, stated here so it is
 * not rediscovered: a test that mounts its OWN QueryClient puts this hook's
 * invalidation on the test client while the service's endpoint entries are still
 * being written to the module singleton, and proves nothing about either. See
 * MarketRail.test.tsx, which mounts the app's own client for precisely this
 * reason.
 */
export function useManualRefresh(sources: readonly RefreshableQuery[]): ManualRefresh {
  const client = useQueryClient()
  const [isManual, setIsManual] = useState(false)

  const refresh = () => {
    setIsManual(true)
    void (async () => {
      try {
        await invalidateEndpointCache(client)
        await Promise.all(sources.map((query) => query.refetch()))
      } catch {
        // Deliberately swallowed, and it is not a lost error. A TanStack
        // `refetch()` RESOLVES on a failed fetch rather than rejecting — the
        // failure lands on the query as `isError`, which is what RefreshBar
        // reads through `useRefreshState` and turns into "Prices did not update"
        // plus a Retry. So a rejection reaching here is something outside that
        // contract, and rethrowing it from a fire-and-forget click handler would
        // only surface as an unhandled rejection nobody can act on. The one
        // thing that must still happen is the line below.
      } finally {
        // Success or failure: `failed` is the honest bar state for a refresh
        // that did not land, and it only resolves once `isManual` is back down.
        // A flag left raised leaves the bar reading "Updating prices…" forever.
        setIsManual(false)
      }
    })()
  }

  return { isManual, refresh }
}
